@if (viewMode === 'list') {
<div class="fiscal-period-container">
    <div class="header-actions">
        <div class="filter-section">
            <label for="subsidiary_filter">Subsidiary:</label>
            <p-select id="subsidiary_filter" [options]="subsidiaryOptions" [(ngModel)]="selectedListSubsidiaryId"
                optionLabel="label" optionValue="value" placeholder="Select Subsidiary"
                (onChange)="onSubsidiaryChange()" [style]="{ width: '250px' }">
            </p-select>
        </div>
        @if (isSuperAdmin) {
            <div class="create-button-section">
                <button type="button" pButton label="Create Fiscal Period" icon="pi pi-plus" 
                    (click)="onCreateClick()" class="p-button-success">
                </button>
            </div>
        }
    </div>

    <lib-dynamic-listing [module]="masterType" [columns]="columnConfigs" [data]="tableData" [actions]="actionConfigs"
        [isLoading]="isLoading" [pageIndex]="pageIndex" [rowsPerPage]="rowsPerPage" [totalCount]="totalCount"
        [totalPages]="totalPages" (actionClicked)="onActionClicked($event)" (pageChanged)="onPageChanged($event)">
    </lib-dynamic-listing>
</div>
} @else {
<div class="fiscal-period-form-container">
    <div class="form-header">
        <h2>{{ isEditMode ? 'Manage Fiscal Period Status' : 'Create Fiscal Period' }}</h2>
    </div>

    <div class="form-content">
        @if (!isEditMode) {
        <!-- Create Form -->
        <form [formGroup]="formGroup" class="fiscal-period-form">
            <div class="form-row">
                <div class="form-field">
                    <label for="subsidiary_id">Subsidiary <span class="required">*</span></label>
                    <p-select id="subsidiary_id" formControlName="subsidiary_id" [options]="subsidiaryOptions"
                        optionLabel="label" optionValue="value" placeholder="Select Subsidiary"
                        [style]="{ width: '100%' }">
                    </p-select>
                </div>
                <div class="form-field">
                    <label for="period_name">Period Name <span class="required">*</span></label>
                    <input type="text" pInputText id="period_name" formControlName="period_name"
                        placeholder="e.g., January 2025" />
                </div>
            </div>

            <div class="form-row">
                <div class="form-field">
                    <label for="start_date">Start Date <span class="required">*</span></label>
                    <p-datepicker id="start_date" formControlName="start_date" dateFormat="dd/mm/yy" [showIcon]="true"
                        placeholder="Start Date" [style]="{ width: '100%' }">
                    </p-datepicker>
                </div>
                <div class="form-field">
                    <label for="end_date">End Date <span class="required">*</span></label>
                    <p-datepicker id="end_date" formControlName="end_date" dateFormat="dd/mm/yy" [showIcon]="true"
                        placeholder="End Date" [style]="{ width: '100%' }">
                    </p-datepicker>
                </div>
            </div>

            <div class="form-row">
                <div class="form-field">
                    <label for="fiscal_year">Fiscal Year <span class="required">*</span></label>
                    <p-inputNumber id="fiscal_year" formControlName="fiscal_year" [useGrouping]="false"
                        placeholder="e.g., 2025" [style]="{ width: '100%' }">
                    </p-inputNumber>
                </div>
                <div class="form-field">
                    <label for="period_type">Period Type <span class="required">*</span></label>
                    <p-select id="period_type" formControlName="period_type" [options]="periodTypeOptions"
                        optionLabel="label" optionValue="value" placeholder="Select Period Type"
                        [style]="{ width: '100%' }">
                    </p-select>
                </div>
            </div>

            <div class="form-actions">
                <button type="button" pButton label="Save" (click)="onFormSubmit()"
                    [disabled]="isLoading || formGroup.invalid"></button>
                <button type="button" pButton label="Cancel" severity="secondary" (click)="onCancel()"></button>
            </div>
        </form>
        } @else {
        <!-- Edit Form - Status Management Only -->
        <div class="period-info">
            <div class="info-row">
                <div class="info-item">
                    <label>Subsidiary</label>
                    <span>{{ getSubsidiaryName(formData?.subsidiary_id) }}</span>
                </div>
                <div class="info-item">
                    <label>Period Name</label>
                    <span>{{ formData?.period_name }}</span>
                </div>
            </div>
            <div class="info-row">
                <div class="info-item">
                    <label>Start Date</label>
                    <span>{{ formData?.start_date | date:'dd/MM/yyyy' }}</span>
                </div>
                <div class="info-item">
                    <label>End Date</label>
                    <span>{{ formData?.end_date | date:'dd/MM/yyyy' }}</span>
                </div>
            </div>
            <div class="info-row">
                <div class="info-item">
                    <label>Fiscal Year</label>
                    <span>{{ formData?.fiscal_year }}</span>
                </div>
                <div class="info-item">
                    <label>Period Type</label>
                    <span class="capitalize">{{ formData?.period_type }}</span>
                </div>
            </div>
        </div>

        <div class="status-section">
            <h3>Status Management</h3>
            <p class="status-info">Status flow: <strong>Open → Closed → Locked</strong>. Locked status is
                irreversible.</p>

            <div class="status-grid">
                <!-- AR Status -->
                <div class="status-item">
                    <label>AR Status (Accounts Receivable)</label>
                    <div class="status-controls">
                        <span class="status-badge" [ngClass]="getStatusClass(formData?.ar_status)">
                            {{ formData?.ar_status || 'open' }}
                        </span>
                        @if (formData?.ar_status !== 'locked') {
                        <p-select [options]="getNextStatusOptions(formData?.ar_status)" [(ngModel)]="newARStatus"
                            optionLabel="label" optionValue="value" placeholder="Change to..."
                            [style]="{ width: '140px' }">
                        </p-select>
                        <button type="button" pButton label="Update" severity="secondary" (click)="onUpdateStatus('ar')"
                            [disabled]="!newARStatus || isLoading"></button>
                        } @else {
                        <span class="locked-info">Status is permanently locked</span>
                        }
                    </div>
                </div>

                <!-- AP Status -->
                <div class="status-item">
                    <label>AP Status (Accounts Payable)</label>
                    <div class="status-controls">
                        <span class="status-badge" [ngClass]="getStatusClass(formData?.ap_status)">
                            {{ formData?.ap_status || 'open' }}
                        </span>
                        @if (formData?.ap_status !== 'locked') {
                        <p-select [options]="getNextStatusOptions(formData?.ap_status)" [(ngModel)]="newAPStatus"
                            optionLabel="label" optionValue="value" placeholder="Change to..."
                            [style]="{ width: '140px' }">
                        </p-select>
                        <button type="button" pButton label="Update" severity="secondary" (click)="onUpdateStatus('ap')"
                            [disabled]="!newAPStatus || isLoading"></button>
                        } @else {
                        <span class="locked-info">Status is permanently locked</span>
                        }
                    </div>
                </div>

                <!-- JV Status -->
                <div class="status-item">
                    <label>JV Status (Journal Voucher)</label>
                    <div class="status-controls">
                        <span class="status-badge" [ngClass]="getStatusClass(formData?.jv_status)">
                            {{ formData?.jv_status || 'open' }}
                        </span>
                        @if (formData?.jv_status !== 'locked') {
                        <p-select [options]="getNextStatusOptions(formData?.jv_status)" [(ngModel)]="newJVStatus"
                            optionLabel="label" optionValue="value" placeholder="Change to..."
                            [style]="{ width: '140px' }">
                        </p-select>
                        <button type="button" pButton label="Update" severity="secondary" (click)="onUpdateStatus('jv')"
                            [disabled]="!newJVStatus || isLoading"></button>
                        } @else {
                        <span class="locked-info">Status is permanently locked</span>
                        }
                    </div>
                </div>
            </div>
        </div>

        <div class="form-actions">
            <button type="button" pButton label="Back to List" severity="secondary" (click)="onCancel()"></button>
        </div>
        }
    </div>
</div>
}